import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { paths } from 'core/routing/paths';
import {
  CONSULTATION_GOALS,
  CONSULTATION_PERIODS,
  CONSULTATION_STATUSES,
  operationsApi,
} from 'features/operations/operations.api';
import { listItems, listMeta } from 'features/operations/operations.helpers';
import { Badge, EmptyState, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { detailsAction, RowActionsCell, RowActionsHeader, RowActionsMenu } from 'shared/components/RowActionsMenu';
import { Pagination } from 'shared/components/Pagination';
import { formatDateTime, formatUserLabel } from 'shared/utilities/format';

const FILTER_KEYS = [
  'reference', 'user_id', 'specialist_id', 'status', 'goal', 'preferred_period',
  'attempt_id', 'created_from', 'created_to', 'scheduled_from', 'scheduled_to',
];

export function ConsultationsPage() {
  const { t, locale, dir } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => {
    const next = { page: Number(searchParams.get('page') || 1), per_page: 20 };
    FILTER_KEYS.forEach((key) => {
      next[key] = searchParams.get(key) || '';
    });
    return next;
  }, [searchParams]);

  const query = useQuery({
    queryKey: queryKeys.consultations(filters),
    queryFn: ({ signal }) => operationsApi.listConsultations(filters, { signal }),
  });
  const specialistsQuery = useQuery({
    queryKey: queryKeys.specialists,
    queryFn: ({ signal }) => operationsApi.listSpecialists({ signal }),
  });

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const items = listItems(query.data);
  const meta = listMeta(query.data);

  if (query.isLoading) return <Skeleton rows={6} />;
  if (query.isError) {
    return <ErrorState title={humanizeApiError(query.error, t)} onRetry={query.refetch} retryLabel={t('actions.retry')} />;
  }

  return (
    <div>
      <PageHeader title={t('consultations.title')} />
      <div className="filter-grid">
        <input className="input" value={filters.reference} onChange={(event) => setFilter('reference', event.target.value)} placeholder={t('consultations.reference')} aria-label={t('consultations.reference')} />
        <input className="input" value={filters.user_id} onChange={(event) => setFilter('user_id', event.target.value)} placeholder={t('consultations.user')} aria-label={t('consultations.user')} />
        <select className="input" value={filters.specialist_id} onChange={(event) => setFilter('specialist_id', event.target.value)} aria-label={t('consultations.specialist')}>
          <option value="">{t('filters.all')}</option>
          {listItems(specialistsQuery.data).map((item) => (
            <option key={item.id} value={item.id}>{item.name_en || item.name_ar || item.name}</option>
          ))}
        </select>
        <select className="input" value={filters.status} onChange={(event) => setFilter('status', event.target.value)} aria-label={t('consultations.status')}>
          <option value="">{t('filters.all')}</option>
          {CONSULTATION_STATUSES.map((status) => <option key={status} value={status}>{t(`consultations.statuses.${status}`)}</option>)}
        </select>
        <select className="input" value={filters.goal} onChange={(event) => setFilter('goal', event.target.value)} aria-label={t('consultations.goal')}>
          <option value="">{t('filters.all')}</option>
          {CONSULTATION_GOALS.map((goal) => <option key={goal} value={goal}>{t(`consultations.goals.${goal}`)}</option>)}
        </select>
        <select className="input" value={filters.preferred_period} onChange={(event) => setFilter('preferred_period', event.target.value)} aria-label={t('consultations.preferredPeriod')}>
          <option value="">{t('filters.all')}</option>
          {CONSULTATION_PERIODS.map((period) => <option key={period} value={period}>{t(`consultations.periods.${period}`)}</option>)}
        </select>
        <input className="input" type="date" value={filters.created_from} onChange={(event) => setFilter('created_from', event.target.value)} aria-label={t('consultations.createdFrom')} />
        <input className="input" type="date" value={filters.created_to} onChange={(event) => setFilter('created_to', event.target.value)} aria-label={t('consultations.createdTo')} />
        <input className="input" type="date" value={filters.scheduled_from} onChange={(event) => setFilter('scheduled_from', event.target.value)} aria-label={t('consultations.scheduledFrom')} />
        <input className="input" type="date" value={filters.scheduled_to} onChange={(event) => setFilter('scheduled_to', event.target.value)} aria-label={t('consultations.scheduledTo')} />
        <button type="button" className="btn btn-secondary" onClick={() => setSearchParams({})}>{t('actions.reset')}</button>
      </div>
      {items.length === 0 ? <EmptyState title={t('empty.consultations')} /> : (
        <>
        <div className="table-wrap desktop-table">
          <table className="data-table has-sticky-actions">
            <caption className="sr-only">{t('consultations.title')}</caption>
            <thead>
              <tr>
                <th>{t('consultations.reference')}</th>
                <th>{t('consultations.user')}</th>
                <th>{t('consultations.status')}</th>
                <th>{t('consultations.specialist')}</th>
                <th>{t('consultations.goal')}</th>
                <th>{t('consultations.preferredPeriod')}</th>
                <th>{t('consultations.scheduledAt')}</th>
                <th>{t('consultations.createdFrom')}</th>
                <RowActionsHeader />
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>{item.reference || item.id}</td>
                  <td>{formatUserLabel(item.user, t)}</td>
                  <td><Badge>{t(`consultations.statuses.${item.status}`)}</Badge></td>
                  <td>{item.specialist?.name_en || item.specialist?.name_ar || item.specialist?.name || '—'}</td>
                  <td>{item.goal ? t(`consultations.goals.${item.goal}`) : '—'}</td>
                  <td>{item.preferred_period ? t(`consultations.periods.${item.preferred_period}`) : '—'}</td>
                  <td>{formatDateTime(item.scheduled_at, locale)}</td>
                  <td>{formatDateTime(item.created_at, locale)}</td>
                  <RowActionsCell
                    label={`${t('actions.rowActions')} #${item.id}`}
                    items={[detailsAction(paths.consultationDetail(item.id), t)]}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ul className="mobile-card-list">
          {items.map((item) => (
            <li key={`card-${item.id}`} className="card">
              <div className="toolbar wrap">
                <strong>{item.reference || item.id}</strong>
                <RowActionsMenu
                  label={`${t('actions.rowActions')} #${item.id}`}
                  items={[detailsAction(paths.consultationDetail(item.id), t)]}
                />
              </div>
              <p>{formatUserLabel(item.user, t)}</p>
              <Badge>{t(`consultations.statuses.${item.status}`)}</Badge>
              <p className="muted">{item.specialist?.name_en || item.specialist?.name_ar || item.specialist?.name || '—'}</p>
              <p className="muted">{formatDateTime(item.scheduled_at, locale)}</p>
            </li>
          ))}
        </ul>
        </>
      )}
      <Pagination
        page={meta.current_page || filters.page}
        lastPage={meta.last_page}
        total={meta.total}
        onPageChange={(page) => setFilter('page', String(page))}
        label={t('pagination.page')}
        ofLabel={t('pagination.of')}
        previousLabel={t('pagination.previous')}
        nextLabel={t('pagination.next')}
        dir={dir}
      />
    </div>
  );
}
